import { StyleSheet, css } from 'aphrodite';
import type { FC } from 'react';

import { Button } from '../../../web/components-v2/Button';
import type { KeywordPin } from '../pins';
import { IconPlus } from './icons';
import { KeywordPinRow } from './KeywordPinRow';

/**
 * Drawer-tab configuration surface for keyword pins.
 *
 * Lifted out of the pre-Stage-2 RightColumn rail so the pinned-card stack
 * (now in CenterColumn) and the chat thread (now in RightColumn) can stay
 * focused on call-time work. The drawer tab shows every pin — auto-send
 * (`before_call`/`on_dial`) and `starter` triggers alike — so the user can
 * configure any pin from a single place.
 */
export type PinConfigPanelProps = {
	pins: KeywordPin[];
	onToggleEnabled: (id: string) => void;
	onDelete: (id: string) => void;
	onEdit: (pin: KeywordPin) => void;
	onNew: () => void;
};

export const PinConfigPanel: FC<PinConfigPanelProps> = ({ pins, onToggleEnabled, onDelete, onEdit, onNew }) => {
	return (
		<div className={css(styles.root)}>
			<header className={css(styles.head)}>
				<span className={css(styles.title)}>Pin Config</span>
				<Button variant='outline' size='sm' onClick={onNew}>
					<IconPlus size={13} />
					New pin
				</Button>
			</header>

			<div className={css(styles.list)}>
				{pins.map(p => (
					<KeywordPinRow
						key={p.id}
						pin={p}
						onToggle={() => onToggleEnabled(p.id)}
						onDelete={() => onDelete(p.id)}
						onEdit={() => onEdit(p)}
					/>
				))}
			</div>
		</div>
	);
};

const styles = StyleSheet.create({
	root: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		// `--bg-elev` matches sibling drawer tabs (ConversationsPanel etc.) so
		// all tabs in the SideDrawer read as the same elevation surface.
		background: 'var(--bg-elev)',
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		height: 52,
		padding: '0 20px',
		borderBottom: '1px solid var(--border)',
		flexShrink: 0,
	},
	title: {
		fontSize: 13,
		fontWeight: 600,
		color: 'var(--ink)',
	},
	list: {
		flex: 1,
		overflowY: 'auto',
		padding: 16,
		display: 'flex',
		flexDirection: 'column',
		gap: 6,
	},
});
